import { isAndroidWebView, isLinux } from '@eventbrite/feature-detection';
import { getExperiment, initializeSdk } from '@eventbrite/statsig';
import { isTicketSelectionExcludedInListings } from './components/EventDetails/ConversionBarContainer/isTicketSelectionExcludedInListings';
import { eventIsAvailable } from './components/EventDetails/WhenAndWhere/DateAndTime/utils';
import {
    LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS,
    LISTING_EARLY_BIRD_PLACEMENT_IMPACT,
    LISTING_PAGE_UNIFYING_REPEATING_EVENTS_CONSUMER_EXPERIMENT,
    LISTING_SEASONAL_AD_BANNER,
    SALES_ENDED_LISTINGS_PAGE_EXPERIMENT,
} from './constants';
import { AppProps, ChildEventProps } from './types';

export const LISTING_PAGE_EXPERIMENTS_LAYER = 'listing_page_experiments';

export const initializeStatsigSdk = async (app: AppProps) => {
    const isRepeatingEvent =
        app.event?.isSeriesParent && !!app.event?.childEvents;
    const compactCheckoutDisqualifications =
        app.event?.compactCheckoutDisqualifications;
    const allowedDevicesForCompactCheckout = !isLinux() || isAndroidWebView();

    await initializeSdk({
        user: {
            userID: app.user.publicId,
            custom: {
                eventId: app.event?.id,
                organizerId: app.organizer?.id,
                ebDomain: app.env?.ebDomain,
                shouldShowCompactCheckout:
                    !isTicketSelectionExcludedInListings({
                        soldOut: compactCheckoutDisqualifications.sold_out,
                        reservedSeating:
                            compactCheckoutDisqualifications.reserved_seating,
                        isSalesEnded:
                            compactCheckoutDisqualifications.is_sales_ended,
                        isRsvp: compactCheckoutDisqualifications.is_rsvp,
                        isPostponed:
                            compactCheckoutDisqualifications.is_postponed,
                        isCanceled:
                            compactCheckoutDisqualifications.is_canceled,
                        moreThanOneTicket:
                            compactCheckoutDisqualifications.more_than_one_ticket,
                        hasWaitlist:
                            compactCheckoutDisqualifications.has_waitlist,
                        hasWaitingRoom:
                            compactCheckoutDisqualifications.has_waiting_room,
                        hasDiscounts:
                            compactCheckoutDisqualifications.has_discounts,
                        hasAddons: compactCheckoutDisqualifications.has_addons,
                        discountCode:
                            compactCheckoutDisqualifications.discount_code,
                        hasDonations:
                            compactCheckoutDisqualifications.has_donations,
                        externalTicketsAreAvailable:
                            app.components?.conversionBar
                                .externalTicketsAreAvailable,
                        salesStatus: app.event?.salesStatus.sales_status,
                        isSeriesParent: app.event?.isSeriesParent,
                        hasInventoryTiers:
                            compactCheckoutDisqualifications.has_inventory_tiers,
                    }) && allowedDevicesForCompactCheckout,
                isRepeatingEvent: isRepeatingEvent,
                onlineEvent: app.event?.isOnlineEvent,
                isSeriesParent: app.event?.isSeriesParent,
                isSeries: !!app.event?.childEvents?.length,
                listed: app.event?.isListed,
                isLive: app.event?.isLive,
                protected: app.event?.isProtected,
                isFree: !app.components.eventDetails.refund.isPaidEvent,
                isPaid: app.components.eventDetails.refund.isPaidEvent,
                isDonation: app.event?.hasDonationTicketsAvailable,
                salesStatus: app.event?.salesStatus.sales_status,
                isExternalTicketAvailable:
                    app.components?.conversionBar.externalTicketsAreAvailable,
                shouldShowRemindMeButton:
                    app.components?.conversionBar.shouldShowRemindMeButton,
                hasDiscountCode: compactCheckoutDisqualifications.discount_code,
                hasAddons: compactCheckoutDisqualifications.has_addons,
                hasDiscount: compactCheckoutDisqualifications.has_discounts,
                hasInventoryTiers:
                    compactCheckoutDisqualifications.has_inventory_tiers,
                hasWaitingRoom:
                    compactCheckoutDisqualifications.has_not_waiting_room ??
                    compactCheckoutDisqualifications.has_waiting_room,
                hasWaitlist: compactCheckoutDisqualifications.has_waitlist,
                isCanceled: compactCheckoutDisqualifications.is_canceled,
                isPostponed: compactCheckoutDisqualifications.is_postponed,
                isRSVP: compactCheckoutDisqualifications.is_rsvp,
                isSalesEnded: compactCheckoutDisqualifications.is_sales_ended,
                hasMoreThanOneTicket:
                    compactCheckoutDisqualifications.more_than_one_ticket,
                totalTicketsCount: app.event?.ticketsInfo.totalTicketsCount,
                isReservedSeating:
                    compactCheckoutDisqualifications.reserved_seating,
                isSoldOut: compactCheckoutDisqualifications.sold_out,
                repeatingEvent:
                    compactCheckoutDisqualifications.repeating_event,
                hasDonatios: compactCheckoutDisqualifications.has_donations,
                venueCountry: app.event?.venue?.country,
                venueRegion:
                    app.event?.venue?.region !== ''
                        ? app.event?.venue?.region
                        : undefined,
                timeZone: app.event?.start?.timezone,
            },
        },
    });
};

export const isEventEligibleForRepeatingEventsConsumerExperiment = (
    isSeriesParent: boolean,
    childEvents: Array<ChildEventProps> | null,
) => {
    const availableEvents: Array<ChildEventProps> =
        childEvents && childEvents.length > 0
            ? childEvents.filter((childEvent) => eventIsAvailable(childEvent))
            : [];
    if (isSeriesParent && availableEvents.length > 0) {
        const experimentValue = getExperiment(
            LISTING_PAGE_UNIFYING_REPEATING_EVENTS_CONSUMER_EXPERIMENT.experiment,
            LISTING_PAGE_UNIFYING_REPEATING_EVENTS_CONSUMER_EXPERIMENT.param,
            LISTING_PAGE_UNIFYING_REPEATING_EVENTS_CONSUMER_EXPERIMENT.defaultValue,
        );
        return experimentValue === true || experimentValue === 'true';
    } else {
        return false;
    }
};

export const getIsSalesEndedListingExperimentEnabled = () => {
    const value = getExperiment(
        SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.experimentName,
        SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.param,
        SALES_ENDED_LISTINGS_PAGE_EXPERIMENT.defaultValue,
    );
    return value === true || value === 'true';
};

export const isEarlyBirdShownAsDiscount = () => {
    const value = getExperiment(
        LISTING_EARLY_BIRD_PLACEMENT_IMPACT.experimentName,
        LISTING_EARLY_BIRD_PLACEMENT_IMPACT.param,
        LISTING_EARLY_BIRD_PLACEMENT_IMPACT.defaultValue,
    );
    return value === true || value === 'true';
};

export const isSeasonalAdBannerShown = () => {
    const value = getExperiment(
        LISTING_SEASONAL_AD_BANNER.experimentName,
        LISTING_SEASONAL_AD_BANNER.param,
        LISTING_SEASONAL_AD_BANNER.defaultValue,
    );
    return value === true || value === 'true';
};

export const isAvoidRechargeDatesOnAllEvents = () => {
    const value = getExperiment(
        LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS.experimentName,
        LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS.param,
        LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS.defaultValue,
    );
    return value === true || value === 'true';
};
